import { Button } from '../../ui/button/button';
import { Table } from '../../ui/table/table';
import { useDispatch } from 'react-redux';
import { useCallback, useEffect, useState } from 'react';
import { dialogActions } from '../slice/dialog-slice';
import './lab-order.scss';
import { PathParams, RoutingPath } from '../../../routes/routing-path';
import { push } from 'connected-react-router';
import { ColorIcon } from '../../ui/color-icon/color-icon';
import { xmlActions } from '../../../xml/slice/xml-slice';
import { ErrorOrder, orderPreparationActions } from '../../../slices/order-preparation-slice';
import { LoadingPopup } from '../../ui/loading/loading-popup';
import { ApiCfOrderGetOne, CfOrderGetOneDataEntity } from '../../../api/cf/api-cf-order-get-one/api-cf-order-get-one';
import { ApiGetCloudFolderOrderFile, CloudFolderGetFileList } from '../../../api/front/cloud-folder/api-get-cloud-folder-order-file';
import { apiActions } from '../../../slices/api-slice';
import { ResponseBase } from '../../../api/response-base';
import { ApiImagesGet, ImagesGetResponse } from '../../../api/front/images/api-images';
import { ApiCfLabOrder, ApiOlcLabOrder } from '../../../api/front/cf-lab-order/cf-lab-order';
import { store } from '../../../app/store';
import { XmlStructureModel, XmlStructureOrderInfoData } from '../../../xml/model/xml-structure-model';

export type LabOrderProps = {
  kijshopCd: string,
  orderList: {
    createDate: string,
    lastName: string,
    shopOrderId: string,
    status: string,
    cfOrderId?: string,
    isOlc?: boolean,
    isOneStop?: boolean,
    // size: string,
    cnt?: number,
  }[],
  callbackEndOrder: () => void,
  retry?: boolean,
};

export type OrderStatus = {
  shopOrderId: string,
  errorCode: string,
};

export const LabOrder = (props: LabOrderProps) => {
  const { kijshopCd, orderList, callbackEndOrder, retry } = props;
  // - Hooks -
  const dispatch = useDispatch();
  // - state -
  const [orderingMessage, setOrderingMessage] = useState('');
  // ワンストップ用 クラウドフォルダデータ取得
  const getOneStopData = useCallback((kijShopId: string, cfOrderId: string) => {
    return new Promise<{
      cfData: CfOrderGetOneDataEntity,
      cfFiles: CloudFolderGetFileList['fileList'],
    }>((resolve, reject) => {
      dispatch(apiActions.run(
        new ApiCfOrderGetOne({
          kijshopCd: kijShopId,
          orderId: cfOrderId,
        },
        ), {
        onSuccess: (cfDataRes: ResponseBase<CfOrderGetOneDataEntity>) => {
          if (Number(cfDataRes.error.errorCode) === 200 && cfDataRes.body.data) {
            const cfData = cfDataRes.body.data;
            dispatch(apiActions.run(
              new ApiGetCloudFolderOrderFile({
                kind: 1,
                kijshopCd,
                orderId: cfData.id,
                thumbOnly: 1,
              }),
              {
                onSuccess: (cfFilesRes: ResponseBase<CloudFolderGetFileList>) => {
                  if (Number(cfFilesRes.error.errorCode) === 200 && cfFilesRes.body.data?.fileList) {
                    const cfFiles = cfFilesRes.body.data.fileList;
                    resolve({
                      cfData,
                      cfFiles,
                    })
                  } else {
                    reject();
                  }
                },
                onError: () => reject(),
              }
            ))
          } else {
            reject();
          }
        },
        onError: () => reject(),
      }))
    })
  }, [dispatch]);

  const getImages = useCallback((
    shopOrderId: string,
  ) => new Promise<ImagesGetResponse[]>((resolve, reject) => {
    dispatch(apiActions.run(
      new ApiImagesGet({
        kijshopCd,
        shopOrderId,
        kind: '6',
      }),
      {
        onSuccess: (res: ResponseBase<ImagesGetResponse[]>) => {
          if ((Number(res.error.errorCode) === 200) && res.body?.data) {
            resolve(res.body.data);
          } else {
            reject();
          }
        },
        onError: () => reject(),
      },
    ));
  }), [kijshopCd, dispatch]);

  // ワンストップラボ発注完了処理
  const CfLabOrder = useCallback((
    shopOrderId: string,
    cfOrderId: string,
  ) => {
    return new Promise<void>((resolve) => {
      dispatch(apiActions.run(new ApiCfLabOrder({
        kijshopCd,
        shopOrderId,
        cfOrderId,
        oneStopOrderUser: store.getState().storage.machineId,
        status: 'ラボ発注中',
      }), {
        onSuccess: (res: ResponseBase<boolean>) => {
          if (res?.body?.data) {
            resolve();
          }
        },
      }));
    });
  }, [dispatch, kijshopCd]);
  
  const OlcLabOrder = useCallback((
    shopOrderId: string,
    cfOrderId: string,
    xml: XmlStructureModel,
  ) => {
    
    return new Promise<void>((resolve) => {
      const arr: XmlStructureOrderInfoData[] = [];
      (xml?.orderInfo?.xml.metaModel.parentSequence || []).forEach((orderInfoData, i) => {
        const v = xml?.orderInfo?.infoData?.find((data) => data.xml.metaModel.id === orderInfoData.id);
        // レタッチ排除
        if (v && v.xml.xmlModel.lnwOrderInfoData?.data?.[0]?.surrogateProcess?.[0]?.$?.id !== '2010') {
          arr.push(v);
        }
      });
      const requestOrderNameList = arr.map((v) => v.xml.viewModel.goods?.name ?? '');
      const isCompImageOrder = xml?.orderInfo?.infoData?.some(
        (v) => v.xml.viewModel.orderMethod?.id === '40'
      ) ? 1 : 0;

      dispatch(apiActions.run(new ApiOlcLabOrder({
        kijshopCd,
        shopOrderId,
        cfOrderId,
        orderUser: store.getState().storage.machineId,
        status: 'ラボ発注中',
        prodAndFolder: requestOrderNameList,
        /* 完成画像入稿（オーダースタイル指定なし）はセンター直行のため、フォルダ選択かつオーダースタイル指定ありの場合のみ納品扱い */
        isDelivery: !!xml?.orderInfo?.infoData?.some((v) => v.xml.viewModel?.selectFolder && !!v.xml.viewModel.purpose?.id),
        isCompImageOrder,
      }), {
        onSuccess: (res: ResponseBase<boolean>) => {
          if (res?.body?.data) {
            resolve();
          }
        },
      }));
    });
  }, [dispatch, kijshopCd]);

  // -- 発注ボタン押下 --
  const handlerClickOrder = useCallback(async () => {
    const enableOrderList: { createDate: string, lastName: string, shopOrderId: string, status: string, cnt: number, cfOrderId?: string, isOlc?: boolean, isOneStop?: boolean }[] = [];
    orderList.forEach((v) => {
      if (v.status === 'ラボ発注可' || retry) {
        enableOrderList.push({
          createDate: v.createDate,
          lastName: v.lastName,
          shopOrderId: v.shopOrderId,
          status: v.status,
          cnt: v.cnt || 1,
          cfOrderId: v.cfOrderId,
          isOlc: v.isOlc,
          isOneStop: v.isOneStop,
        });
      }
    });
    if (!enableOrderList.length) {
      dispatch(dialogActions.pushMessage({
        title: '確認',
        message: ['発注できる注文がありません。'],
        buttons: [{
          label: 'OK',
          callback: () => {
            dispatch(dialogActions.pop());
          },
        }],
      }));
      return;
    }
    dispatch(dialogActions.pushMessage({
      title: '確認',
      message: [
        '発注処理を行います。よろしいですか？',
      ],
      buttons: [
        {
          label: 'いいえ',
          callback: () => {
            dispatch(dialogActions.pop());
          },
        },
        {
          label: 'はい',
          callback: async () => {
            dispatch(dialogActions.pop());
            const errorList: ErrorOrder[] = [];
            for (let i = 0; i < enableOrderList.length; i++) {
              setOrderingMessage(`ラボ発注を実施中です。（${i + 1}/${enableOrderList.length}）`);
              const orderData = enableOrderList[i];
              // ワンストップ用事前処理
              if (orderData.cfOrderId) {
                const {
                  cfData,
                } = await getOneStopData(
                  kijshopCd,
                  orderData.cfOrderId,
                );
                if (orderData.isOneStop) {
                  const result = await new Promise<boolean>((resolve) => {
                    dispatch(xmlActions.oneStop({
                      kijshopCd,
                      shopOrderId: orderData.shopOrderId,
                      cfData,
                      // 一括発注では取扱終了ラミの変換を行わず、エラーオーダーとして残す
                      isBulkOrder: true,
                      callback: {
                        success: () => resolve(true),
                        error: (e) => {
                          errorList.push({
                            ...orderData,
                            // convertErrorStatus で 'irregular' は「通信エラー」と表示されるため、
                            // 通信と無関係な中断理由(ラミネート/バリデーション)は 'xml'(「エラー」)のままにする
                            errorStatus: {
                              code: 'xml',
                              message: e?.length ? e : ['XMLの取得に失敗しました'],
                            },
                            cnt: (orderData.cnt || 1) + 1,
                          });
                          resolve(false)
                        },
                      }
                    }));
                  });
                  // 発注処理がエラーの場合はCF側のステータス更新を行わない(OLC側の result 判定と同様)
                  if (result && cfData.isSelectedDownload) {
                    await CfLabOrder(orderData.shopOrderId, orderData.cfOrderId);
                  }
                } else {
                  const result = await new Promise<XmlStructureModel | false>((resolve) => {
                    dispatch(xmlActions.olc({
                      kijshopCd,
                      shopOrderId: orderData.shopOrderId,
                      cfData,
                      // 一括発注では取扱終了ラミの変換を行わず、エラーオーダーとして残す
                      isBulkOrder: true,
                      callback: {
                        success: ({xml}) => resolve(xml),
                        error: (e) => {
                          errorList.push({
                            ...orderData,
                            // convertErrorStatus で 'irregular' は「通信エラー」と表示されるため、
                            // 通信と無関係な中断理由(ラミネート/バリデーション)は 'xml'(「エラー」)のままにする
                            errorStatus: {
                              code: 'xml',
                              message: e?.length ? e : ['XMLの取得に失敗しました'],
                            },
                            cnt: (orderData.cnt || 1) + 1,
                          });
                          resolve(false)
                        },
                      }
                    }));
                  });
                  if (result) {
                    await OlcLabOrder(orderData.shopOrderId, orderData.cfOrderId, result);
                  }
                }
                continue;
              }
              await new Promise<void>((resolve) => {
                dispatch(xmlActions.shopOrder({ kijshopCd, shopOrderId: orderData.shopOrderId }).serverLabOrder({
                  success: () => {
                    resolve();
                  },
                  error: (e, isCenterError) => {
                    if (isCenterError) {
                      errorList.push({
                        ...orderData,
                        errorStatus: {
                          code: '406',
                          message: e,
                        },
                        cnt: (orderData.cnt || 1) + 1,
                      });

                    }
                    errorList.push({
                      ...orderData,
                      // convertErrorStatus で 'irregular' は「通信エラー」と表示されるため、
                      // 通信と無関係な中断理由(ラミネート/バリデーション)は 'xml'(「エラー」)にする
                      errorStatus: {
                        code: 'xml',
                        message: e?.length ? e : ['XMLの取得に失敗しました'],
                      },
                      cnt: (orderData.cnt || 1) + 1,
                    });
                    resolve();
                  },
                  // 一括発注では取扱終了ラミの変換を行わず、エラーオーダーとして残す
                }, undefined, { isBulkOrder: true }));
              });
            }
            dispatch(dialogActions.pop());
            setOrderingMessage('');
            if (errorList.length) {
              dispatch(orderPreparationActions.setErrorOrderList([...errorList]));
              dispatch(push(RoutingPath.cnv.uploadManager({ kijshopCd })));
            } else {
              dispatch(dialogActions.pushMessage({
                title: '確認',
                message: [
                  '発注処理を行いました',
                  '',
                  '受注完了メールに納品日が記載されていますので',
                  '合わせてご確認ください',
                ],
                buttons: [{
                  label: 'OK',
                  callback: () => {
                    dispatch(dialogActions.pop());
                    callbackEndOrder();
                  },
                }],
              }));
            }
          },
        },
      ],
    }));
  }, [kijshopCd, orderList, retry]);

  // -- 閉じるボタン押下 --
  const handlerClickClose = useCallback(() => {
    dispatch(dialogActions.pop());
  }, []);

  const orderData = [...orderList].map((v) => [
    v.createDate || '',
    v.lastName || '',
    v.shopOrderId,
    <div className="order_status">
      <div className="order_status__inner">
        <ColorIcon color={v.status} />{v.status}
      </div>
    </div>,
    <div style={{ textAlign: 'center' }}>{v.status === 'ラボ発注可' ? '○' : '×'}</div>,
  ]);
  return (
    <div className='dialog_contents lab_order'>
      <div className='dialog_contents__body'>
        <Table
          head={['作成日時', 'お客様名', '注文番号', retry ? '状態' : 'ステータス', '発注可否']}
          body={orderData}
        />
      </div>
      <div className='dialog_contents__footer'>
        <Button
          label='キャンセル'
          onClick={handlerClickClose}
        />
        <Button
          label='発注'
          onClick={handlerClickOrder}
          color='warning'
        // disabled={!}
        />
      </div>
      {orderingMessage ? (<LoadingPopup label={orderingMessage} />) : (<></>)}
    </div>
  );
};
